/* Carte d'un contact dans une colonne du pipeline. */
import React from 'react';
import { AlertCircle, AlertTriangle, Building2, Globe, Link2, User, Users } from 'lucide-react';
import type { AccountState, CrmContact } from './types';
import { type GroupStats, rolesSummary } from './participants';
import { SibelgaStatusTags } from './SibelgaStatus';
import { ROLE_BADGES, ROLE_LABELS, displayName, isGroupCard } from './types';
import { missingForNextStage } from './validation';
import { StatusDot, formatDateFr } from './ui';

interface Props {
  contact: CrmContact;
  account?: AccountState;
  /** Chiffres du groupe (fiches participants), pour la carte d'un groupe. */
  groupStats?: GroupStats;
  onOpen: (c: CrmContact) => void;
  onDragStart: (e: React.DragEvent, c: CrmContact) => void;
  onDragEnd: () => void;
}

export function ContactCard({ contact: c, account, groupStats, onOpen, onDragStart, onDragEnd }: Props) {
  const groupCard = isGroupCard(c);
  const missing = missingForNextStage(c);
  const hasMechanical = c.meters.some(m => m.meter_type === 'mecanique');

  const stageInfo = (() => {
    switch (c.stage) {
      case 'contract_sent':
        return c.contract_sent_at ? `Envoyé le ${formatDateFr(c.contract_sent_at)}` : null;
      case 'contract_signed':
        return c.contract_signed_at ? `Signé le ${formatDateFr(c.contract_signed_at)}` : null;
      case 'sibelga':
        return c.sibelga_encoded_at
          ? `Encodé le ${formatDateFr(c.sibelga_encoded_at)}${c.sharing_start_date ? ` · début ${formatDateFr(c.sharing_start_date)}` : ''}`
          : null;
      case 'active':
        return c.sharing_start_date ? `Partage depuis le ${formatDateFr(c.sharing_start_date)}` : null;
      case 'lead':
        if (c.questionnaire_sent_at && !c.questionnaire_submission_id) return `Formulaire envoyé le ${formatDateFr(c.questionnaire_sent_at)} · en attente`;
        if (c.questionnaire_sent_at && c.questionnaire_submission_id) return 'Formulaire d’adhésion reçu';
        return null;
      default:
        return null;
    }
  })();

  return (
    <div
      draggable
      onDragStart={e => onDragStart(e, c)}
      onDragEnd={onDragEnd}
      onClick={() => onOpen(c)}
      className="bg-white border border-neutral-200 rounded-xl p-3 cursor-grab active:cursor-grabbing hover:border-neutral-300 hover:shadow-sm transition-all select-none"
    >
      <div className="flex items-start gap-2">
        <div className="w-7 h-7 rounded-lg bg-neutral-100 flex items-center justify-center shrink-0">
          {groupCard
            ? <Users className="w-3.5 h-3.5 text-neutral-500" />
            : c.entity_type === 'morale'
              ? <Building2 className="w-3.5 h-3.5 text-neutral-500" />
              : <User className="w-3.5 h-3.5 text-neutral-500" />}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-neutral-900 truncate">{displayName(c)}</p>
          <p className="text-[11px] text-neutral-400 truncate">{c.email || 'Pas d’e-mail'}</p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-1 mt-2">
        {c.role && !groupCard && (
          <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${ROLE_BADGES[c.role]}`}>{ROLE_LABELS[c.role]}</span>
        )}
        {groupCard ? (
          <>
            <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-violet-50 text-violet-700">
              Groupe · {groupStats?.eans ?? c.meters.length} EAN
            </span>
            {!!groupStats?.companies && (
              <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-neutral-100 text-neutral-600">
                {groupStats.companies} société{groupStats.companies > 1 ? 's' : ''}
              </span>
            )}
          </>
        ) : c.meters.length > 0 && (
          <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-neutral-100 text-neutral-600">{c.meters.length} EAN</span>
        )}
        {!groupCard && c.groupe && (
          <span className="inline-flex items-center gap-0.5 text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-violet-50 text-violet-700">
            <Users className="w-2.5 h-2.5" />{c.groupe}
          </span>
        )}
        {c.source === 'formulaire' && (
          <span className="inline-flex items-center gap-0.5 text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-neutral-100 text-neutral-600">
            <Globe className="w-2.5 h-2.5" />Formulaire
          </span>
        )}
        {c.stage === 'sibelga' && <SibelgaStatusTags value={c.sibelga_status ?? []} />}
        {hasMechanical && (
          <span className="inline-flex items-center gap-0.5 text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-700">
            <AlertTriangle className="w-2.5 h-2.5" />Compteur mécanique
          </span>
        )}
      </div>

      {groupCard && groupStats && <p className="text-[11px] text-neutral-500 mt-2">{rolesSummary(groupStats.roles)}</p>}
      {stageInfo && !groupCard && <p className="text-[11px] text-neutral-500 mt-2">{stageInfo}</p>}
      {c.stage !== 'active' && c.waiting_on && (
        <p className={`flex items-center gap-1 text-[11px] mt-2 ${c.waiting_on === 'sunisup' ? 'text-amber-800 font-medium' : 'text-neutral-500'}`}
          title={c.waiting_note ?? undefined}>
          <span className={`w-1.5 h-1.5 rounded-full ${c.waiting_on === 'sunisup' ? 'bg-amber-500' : 'bg-neutral-400'}`} />
          {c.waiting_on === 'sunisup' ? 'À traiter par Sun Is Up' : 'En attente du client'}
          {c.waiting_note ? <span className="font-normal text-neutral-500 truncate">· {c.waiting_note}</span> : null}
        </p>
      )}
      {c.merged_into_id && (
        <p className="flex items-center gap-1 text-[11px] text-violet-700 mt-2"><Link2 className="w-3 h-3" />Rattaché au groupe « {c.groupe} »</p>
      )}

      {missing.length > 0 && (
        <p className="flex items-center gap-1 text-[11px] text-amber-700 mt-2" title={missing.map(m => m.label).join(', ')}>
          <AlertCircle className="w-3 h-3 shrink-0" />
          {missing.length} champ{missing.length > 1 ? 's' : ''} à compléter
        </p>
      )}

      {c.stage === 'active' && (
        <div className="flex flex-wrap gap-x-2.5 gap-y-1 mt-2 pt-2 border-t border-neutral-100">
          <StatusDot on={account === 'approved'} pending={account === 'pending'} label="Compte"
            title={account === 'approved' ? 'Accès plateforme activé' : account === 'pending' ? 'Compte créé, en attente de validation' : 'Aucun accès plateforme créé'} />
          <StatusDot on={!!c.onboarding_sent_at} label="Onboarding" />
        </div>
      )}
    </div>
  );
}
